/**
 * SCRUM-1010 QC, 21 Sep — what order the station list comes in.
 *
 * Two rules, applied after the grid's own sort:
 *
 * 1. Rows still being created stay at the bottom, whatever the sort. A row
 *    that has never been written has no place among rows ordered by their
 *    values, and it must not move under the user as they fill it in.
 * 2. Rows that still need something lift to the top — "make those need action
 *    cell to the top, so user no need to look through the list on which to
 *    edit" (Wei Woon). On a list of 490 stations the three with an empty
 *    Sub Area are otherwise found by scrolling.
 *
 * The lift is deliberately not applied when the user has chosen a sort: a
 * column they clicked is an instruction, and quietly reordering around it
 * would make the sort look broken. The caller decides by passing
 * `needsAction: null`.
 *
 * Both groups keep the order they arrived in, so within "needs action" the
 * grid's sort still holds.
 */

export interface StationRowOrderOptions<T> {
  /** A row that has never been saved — negative id, or flagged. */
  isNew: (row: T) => boolean
  /** Rows to lift, or null to leave the order alone. */
  needsAction?: ((row: T) => boolean) | null
}

export const orderStationRows = <T,>(
  rows: T[],
  { isNew, needsAction }: StationRowOrderOptions<T>,
): T[] => {
  const list = Array.isArray(rows) ? rows : []
  const hasNew = list.some(isNew)
  if (!hasNew && !needsAction) return rows

  const saved = hasNew ? list.filter((row) => !isNew(row)) : list
  const ordered = needsAction
    ? [...saved.filter(needsAction), ...saved.filter((row) => !needsAction(row))]
    : saved

  if (!hasNew) return ordered

  // Newest first among the drafts, which is the order they were added in.
  const drafts = list
    .filter(isNew)
    .sort((a, b) => Number((b as any)?.id) - Number((a as any)?.id))

  return [...ordered, ...drafts]
}
